import { useCurrentMarketLocation, useDownload, useSnackbar } from '@e2m-customer-portal/hooks';
import { getMonthNumber } from '@e2m-customer-portal/shared';
import {
  Dialog,
  DialogProps,
  makeMonthSelection,
  MonthSelection,
  MonthSelector,
  useMonthSelection,
} from '@e2m-customer-portal/ui';
import { Button, CircularProgress, DialogContent, Typography } from '@mui/material';
import clsx from 'clsx';
import {
  eachMonthOfInterval,
  format,
  formatISO,
  getTime,
  Interval,
  isSameMonth,
  subMilliseconds,
  subYears,
} from 'date-fns';
import { BillingType, Month } from 'generated/graphql';
import { MouseEvent, ReactElement, SyntheticEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeApiUrl } from '../../utils';

interface CSVDialogProps {
  open: boolean;
  onClose: DialogProps['onClose'];
}

function fmtIntervals(intervals: Interval[]): string {
  return intervals
    .map((int) => {
      let result = format(int.start, 'MM.yy');
      if (!isSameMonth(int.start, subMilliseconds(int.end, 1))) {
        result += format(subMilliseconds(int.end, 1), '-MM.yy');
      }
      return result;
    })
    .join('_');
}

export function CSVDialog({ open, onClose }: Readonly<CSVDialogProps>): ReactElement<CSVDialogProps> {
  const [t] = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { showError } = useSnackbar();

  const [dataSelection, setDataSelection] = useState<'mscons' | 'trades'>('mscons');

  const [lastYearSelectedMonths, setLastYearSelectedMonths] = useState(makeMonthSelection(false));
  const [thisYearSelectedMonths, setThisYearSelectedMonths] = useState(makeMonthSelection(false));
  const intervals = useMonthSelection(lastYearSelectedMonths, thisYearSelectedMonths);
  const months = intervals.flatMap((interval) =>
    eachMonthOfInterval(interval).filter((date) => getTime(interval.end) !== getTime(date)),
  );

  const marketLocation = useCurrentMarketLocation();
  const { clickHandler } = useDownload();

  const handleDownload = (event: MouseEvent<HTMLAnchorElement>): void => {
    setIsLoading(true);
    clickHandler()(event)
      .catch(() => showError(t('SHARED_ERROR_UNKNOWN')))
      .finally(() => setIsLoading(false));
  };

  const handleClose: DialogProps['onClose'] =
    onClose &&
    ((e, reason) => {
      if (reason !== 'backdropClick') {
        onClose(e, reason);
      }
    });

  const lastYearUnavailableMonths: MonthSelection = marketLocation.billingTypes
    .filter((type) => type.year === subYears(new Date(), 1).getFullYear())
    .reduce(
      (selection, currentBillingType) => ({
        ...selection,
        [getMonthNumber(currentBillingType.month)]: dataSelection === 'trades' && !currentBillingType.isOpenBook,
      }),
      {} as MonthSelection,
    );

  const thisYearUnavailableMonths: MonthSelection = (
    marketLocation.billingTypes.length
      ? marketLocation.billingTypes.filter((type) => type.year === new Date().getFullYear())
      : (Object.values(Month).map((month) => ({
          isOpenBook: false,
          month,
          year: new Date().getFullYear(),
        })) as readonly BillingType[])
  ).reduce((selection, currentBillingType) => {
    const month = getMonthNumber(currentBillingType.month);
    return {
      ...selection,
      [month]: month > new Date().getMonth() || (dataSelection === 'trades' && !currentBillingType.isOpenBook),
    };
  }, {} as MonthSelection);

  useEffect(() => {
    setLastYearSelectedMonths(makeMonthSelection(lastYearSelectedMonths, lastYearUnavailableMonths));
    setThisYearSelectedMonths(makeMonthSelection(thisYearSelectedMonths, thisYearUnavailableMonths));
  }, [dataSelection]);

  const href =
    dataSelection === 'trades'
      ? makeApiUrl('csv-download/trades.csv', {
          marketLocationNr: marketLocation.number,
          months: months.map((month) => formatISO(month).split('T')[0]),
        })
      : makeApiUrl('csv-download/feed.csv', {
          marketLocationId: marketLocation.id,
          months: months.map((month) => formatISO(month)),
        });

  const filename = `${dataSelection === 'trades' ? 'Trades' : 'Messdaten'}_${marketLocation.name.replaceAll(/\s/g, '_')}_${fmtIntervals(intervals)}.csv`;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={t('CSV_DOWNLOAD_MODAL_TITLE')}
      actions={
        <>
          <Button
            variant="outlined"
            color="primary"
            onClick={onClose && ((e: SyntheticEvent) => onClose(e, 'cancelClick'))}
          >
            {t('SHARED_CANCEL')}
          </Button>
          <Button
            component="a"
            variant="contained"
            color="primary"
            href={href}
            download={filename}
            onClick={handleDownload}
            disabled={intervals.length === 0 || isLoading}
            startIcon={isLoading && <CircularProgress size="1rem" />}
          >
            {t('CSV_DOWNLOAD_START_BUTTON')}
          </Button>
        </>
      }
    >
      <DialogContent className="pt-0 grid grid-cols-2 gap-4">
        {marketLocation.billingTypes.some((type) => type.isOpenBook) && (
          <div className="flex flex-col col-span-2 *:flex *:gap-x-1 *:accent-green-200">
            <label>
              <input
                type="radio"
                name="data-selection"
                value="mscons"
                defaultChecked={dataSelection === 'mscons'}
                onClick={() => setDataSelection('mscons')}
              />
              <span>{t('CSV_DOWNLOAD_DATA_SELECTION_MSCONS_DATA')}</span>
            </label>
            <label>
              <input
                type="radio"
                name="data-selection"
                value="trades"
                defaultChecked={dataSelection === 'trades'}
                onClick={() => setDataSelection('trades')}
              />
              <span>{t('CSV_DOWNLOAD_DATA_SELECTION_TRADES')}</span>
            </label>
          </div>
        )}
        <div className="col-span-2 md:col-span-1">
          <Typography variant="h4">{t('CSV_DOWNLOAD_YEAR', { date: subYears(new Date(), 1) })}</Typography>
          <MonthSelector
            disabled={lastYearUnavailableMonths}
            selected={lastYearSelectedMonths}
            onChange={setLastYearSelectedMonths}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Typography variant="h4">{t('CSV_DOWNLOAD_YEAR', { date: new Date() })}</Typography>
          <MonthSelector
            disabled={thisYearUnavailableMonths}
            selected={thisYearSelectedMonths}
            onChange={setThisYearSelectedMonths}
          />
        </div>
        <div className={clsx('grid col-span-2 gap-4', { invisible: dataSelection !== 'mscons' })}>
          <Typography variant="body5">{t('CSV_DOWNLOAD_DISCLAIMER')}</Typography>
          <div>
            <h3 className="mt-0">{t('CSV_DOWNLOAD_MSCONS_INFO_TITLE')}</h3>
            <table className="w-full border-spacing-x-2 border-spacing-y-0 text-xs">
              <thead>
                <tr>
                  <th className="text-start">{t('CSV_DOWNLOAD_MSCONS_INFO_CODE')}</th>
                  <th className="text-start">{t('CSV_DOWNLOAD_MSCONS_INFO_EXPLANATION')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="align-top font-semibold text-chart-mscons-220">220</td>
                  <td>{/.*(?=\s\[)/.exec(t('MARKET_LOCATION_FEED_IN_MSCONS_CODE_220'))}</td>
                </tr>
                <tr>
                  <td className="align-top font-semibold text-chart-mscons-Z18">Z18</td>
                  <td>{/.*(?=\s\[)/.exec(t('MARKET_LOCATION_FEED_IN_MSCONS_CODE_Z18'))}</td>
                </tr>
                <tr>
                  <td className="align-top font-semibold text-chart-mscons-67">67</td>
                  <td>{/.*(?=\s\[)/.exec(t('MARKET_LOCATION_FEED_IN_MSCONS_CODE_67'))}</td>
                </tr>
                <tr>
                  <td className="align-top font-semibold text-chart-mscons-missing">—</td>
                  <td>{t('MARKET_LOCATION_FEED_IN_MSCONS_CODE_OTHER')}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
